/**
 * Decode JWT token and extract claims
 */
export interface JwtClaims {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  organizationId: string;
  organizationName: string;
  restaurantId: string;
  restaurantName: string;
  role: string;
  exp: number;
}

export function decodeJwt(token: string): JwtClaims | null {
  try {
    if (!token || typeof token !== 'string') {
      console.error('Invalid token: token is null, undefined, or not a string');
      return null;
    }

    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('Invalid token format: JWT must have 3 parts separated by dots');
      return null;
    }

    const base64Url = parts[1];
    if (!base64Url) {
      console.error('Invalid token: payload part is missing');
      return null;
    }

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    const payload = JSON.parse(jsonPayload);

    return {
      userId: payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'],
      email: payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'],
      firstName: payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname'],
      lastName: payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname'],
      organizationId: payload['OrganizationId'],
      organizationName: payload['OrganizationName'],
      restaurantId: payload['RestaurantId'],
      restaurantName: payload['RestaurantName'],
      role: payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'],
      exp: payload['exp'],
    };
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
}

export function isTokenExpired(token: string): boolean {
  const claims = decodeJwt(token);
  if (!claims) return true;

  const now = Math.floor(Date.now() / 1000);
  return claims.exp < now;
}
